import { FC } from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { useQuery } from '@apollo/client';
import { AI_INTERVIEW_RESULT } from './gql';
import { formatDateUS, formatTime } from '../../utils/commonFn';
// Import the console stylesheet so the modal's ai-console__interview-* classes are
// styled wherever it's used — including job-application cards that don't otherwise
// load this sheet. (Global SCSS; webpack dedupes the import.)
import './ai-recruiter-console.scss';

// ---------------------------------------------------------------------------
// Props contract
// ---------------------------------------------------------------------------

export interface InterviewResultModalProps {
  isOpen: boolean;
  pipelineEntryId: string;
  candidateName?: string | null;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Shape returned by AI_INTERVIEW_RESULT
// ---------------------------------------------------------------------------

interface InterviewResult {
  id: string;
  pipelineEntryId: string | null | undefined;
  recommendation: string | null | undefined;
  score: number | null | undefined;
  summary: string | null | undefined;
  transcript: string | null | undefined;
  recordingUrl: string | null | undefined;
  sentiment: string | null | undefined;
  callSuccessful: boolean | null | undefined;
  inVoicemail: boolean | null | undefined;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Verdict tone helpers — mirror ConsoleInterviewBadge's leading-verb parsing.
// ---------------------------------------------------------------------------

type VerdictTone = 'positive' | 'neutral' | 'danger';

const VERDICT_DISPLAY: Record<string, { tone: VerdictTone; label: string }> = {
  advance: { tone: 'positive', label: 'Advance' },
  review: { tone: 'neutral', label: 'Review' },
  reject: { tone: 'danger', label: 'Reject' },
};

function parseVerdict(
  recommendation: string | null | undefined,
): { tone: VerdictTone; label: string } {
  if (!recommendation || recommendation.trim() === '') {
    return { tone: 'neutral', label: 'Review' };
  }
  // The model returns e.g. "advance, strong communication..." — the first token
  // carries a trailing comma, so match only the leading alphabetic run.
  const firstWord =
    recommendation.trim().toLowerCase().match(/^[a-z]+/)?.[0] ?? '';
  return VERDICT_DISPLAY[firstWord] ?? { tone: 'neutral', label: 'Review' };
}

function buildTimestampLabel(createdAt: string): string {
  return `${formatDateUS(createdAt)} ${formatTime(createdAt)}`;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const InterviewResultModal: FC<InterviewResultModalProps> = ({
  isOpen,
  pipelineEntryId,
  candidateName,
  onClose,
}) => {
  const { data, loading, error } = useQuery<{
    aiInterviewResult: InterviewResult | null;
  }>(AI_INTERVIEW_RESULT, {
    variables: { pipelineEntryId },
    skip: !isOpen || !pipelineEntryId,
    fetchPolicy: 'cache-and-network',
  });

  const result = data?.aiInterviewResult ?? null;
  const verdict = parseVerdict(result?.recommendation);

  const headerTitle = candidateName?.trim()
    ? `${candidateName} — Interview result`
    : 'Interview result';

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" style={{ maxWidth: '640px' }}>
      <ModalHeader toggle={onClose} style={{ borderBottom: '1px solid #eeeeee' }}>
        <div style={{ fontWeight: 700, fontSize: '1rem', color: '#33475b' }}>
          {headerTitle}
        </div>
      </ModalHeader>

      <ModalBody style={{ padding: '20px 24px' }}>
        {/* Loading state */}
        {loading && !result && (
          <p className="text-muted text-center py-4" style={{ fontSize: '0.875rem' }}>
            Loading…
          </p>
        )}

        {/* Error state */}
        {!loading && error && (
          <p className="text-danger text-center py-4" style={{ fontSize: '0.875rem' }}>
            Could not load the interview result. Please try again.
          </p>
        )}

        {/* Empty state */}
        {!loading && !error && !result && (
          <p className="text-muted text-center py-4" style={{ fontSize: '0.875rem' }}>
            No interview result recorded yet.
          </p>
        )}

        {/* Result */}
        {result && (
          <>
            {/* Verdict header — pill + score + full recommendation text */}
            <div className="ai-console__interview-verdict">
              <span
                className={`ai-console__interview-badge ai-console__interview-badge--${verdict.tone}`}
              >
                {verdict.label}
              </span>
              {result.score != null && (
                <span className="ai-console__interview-score">
                  {result.score}
                  <span className="ai-console__interview-score-max">/10</span>
                </span>
              )}
              <span className="ai-console__interview-timestamp">
                {buildTimestampLabel(result.createdAt)}
              </span>
            </div>

            {result.recommendation && (
              <p className="ai-console__interview-recommendation">
                {result.recommendation}
              </p>
            )}

            {/* Summary */}
            {result.summary && (
              <div className="ai-console__interview-section">
                <h6 className="ai-console__interview-heading">Summary</h6>
                <p className="ai-console__interview-summary">{result.summary}</p>
              </div>
            )}

            {/* Chips — only render each when its value is non-null */}
            {(result.sentiment != null ||
              result.callSuccessful != null ||
              result.inVoicemail != null) && (
              <div className="ai-console__interview-chips">
                {result.sentiment != null && (
                  <span className="ai-console__interview-chip">
                    Sentiment: {result.sentiment}
                  </span>
                )}
                {result.callSuccessful != null && (
                  <span
                    className={`ai-console__interview-chip ai-console__interview-chip--${
                      result.callSuccessful ? 'positive' : 'danger'
                    }`}
                  >
                    Call {result.callSuccessful ? 'successful' : 'unsuccessful'}
                  </span>
                )}
                {result.inVoicemail != null && (
                  <span className="ai-console__interview-chip">
                    {result.inVoicemail ? 'Reached voicemail' : 'Live answer'}
                  </span>
                )}
              </div>
            )}

            {/* Recording */}
            {result.recordingUrl && (
              <div className="ai-console__interview-section">
                <h6 className="ai-console__interview-heading">Recording</h6>
                <audio
                  controls
                  src={result.recordingUrl}
                  className="ai-console__interview-audio"
                >
                  Your browser does not support the audio element.
                </audio>
              </div>
            )}

            {/* Transcript */}
            {result.transcript && (
              <div className="ai-console__interview-section">
                <h6 className="ai-console__interview-heading">Transcript</h6>
                <pre className="ai-console__interview-transcript">
                  {result.transcript}
                </pre>
              </div>
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default InterviewResultModal;
